import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import { currencyInput } from '@/_schemas/generic';

import {
  useCommissionBulkCreateMutation,
  useCommissionBulkDeleteMutation,
  useCommissionBulkUpdateMutation,
} from '../commission-apis';
import type { AgreementFormSchema } from '../schemas';
import type { CommissionsFormSchema, CommissionsQuerySchema } from '../schemas/commissions-schemas';

export function useAgreementCommissions(
  form: UseFormReturn<AgreementFormSchema>,
  oldCommissions: CommissionsQuerySchema[]
) {
  const commissionsDeleteMutation = useCommissionBulkDeleteMutation();
  const commissionsUpdateMutation = useCommissionBulkUpdateMutation();
  const commissionsCreateMutation = useCommissionBulkCreateMutation();

  const manageCommissions = useCallback(async () => {
    if (!form?.formState?.dirtyFields?.agreement_commissions) {
      return;
    }
    const commissions = form.getValues('agreement_commissions') || [];
    const id = form.getValues('id');

    const created = commissions.filter((t) => t.id < 0);

    const deleted = oldCommissions.filter((ot) => !commissions.find((t) => t.id === ot.id));

    const updated = commissions.filter((t) => t.id > 0);

    if (deleted && deleted.length > 0) {
      await commissionsDeleteMutation.mutateAsync({
        items: deleted.map((t) => t.id!),
      });
    }

    if (updated.length > 0) {
      await commissionsUpdateMutation.mutateAsync({
        items: updated.map((c) => ({
          ...c,
          team_member_id: c.team_member?.id ?? -1,
          // commission_plan_id: c.commission_plan?.id ?? null,
          one_time_paid_amount: currencyInput.formToUpdateConverter(c.one_time_paid_amount),
          recurring_paid_amount: currencyInput.formToUpdateConverter(c.recurring_paid_amount),
        })),
      });
    }

    let newlyCreated: CommissionsFormSchema[] = [];
    if (created.length > 0) {
      const res = await commissionsCreateMutation.mutateAsync({
        items: created.map((c) => ({
          ...c,
          agreement_id: Number(id),
          team_member_id: c.team_member?.id ?? -1,
          // commission_plan_id: c.commission_plan?.id ?? null,
          one_time_paid_amount: currencyInput.formToUpdateConverter(c.one_time_paid_amount),
          recurring_paid_amount: currencyInput.formToUpdateConverter(c.recurring_paid_amount),
        })),
      });

      newlyCreated = res.map((r, idx) => {
        const original = created[idx] ?? ({} as CommissionsFormSchema);
        return {
          ...original,
          id: r.id,
        } as CommissionsFormSchema;
      });
    }

    const latestTiers = [...updated, ...newlyCreated];
    form.setValue('agreement_commissions', latestTiers, { shouldDirty: false });
    return latestTiers;
  }, [form, commissionsDeleteMutation, commissionsCreateMutation, commissionsUpdateMutation, oldCommissions]);

  return {
    manageCommissions,
    isManageCommissionPending:
      commissionsDeleteMutation.isPending || commissionsUpdateMutation.isPending || commissionsCreateMutation.isPending,
  };
}
